// DispatchHub push-notification client helper.
// Owns permission + subscription lifecycle. Kept UI-agnostic so both the
// InstallPrompt banner and a Settings page can call it.
import client from "@/api/client";

const urlB64ToUint8Array = (b64) => {
  const padding = "=".repeat((4 - b64.length % 4) % 4);
  const s = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export const pushPermission = () => (pushSupported() ? Notification.permission : "unsupported");

export async function currentSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
}

/**
 * Ask the user for permission (if not already granted / denied) and
 * subscribe them to web-push. On success the subscription is stored
 * server-side keyed by (user_mobile, endpoint) so future stage
 * transitions can dispatch role-scoped notifications to this device.
 * Returns { ok: true } on success, { ok:false, reason } on failure.
 */
export async function enablePush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };

  // 1. Permission
  let perm = Notification.permission;
  if (perm === "default") perm = await Notification.requestPermission();
  if (perm !== "granted")  return { ok: false, reason: "denied" };

  // 2. VAPID public key
  const { data } = await client.get("/push/vapid-public-key");
  if (!data?.publicKey) return { ok: false, reason: "server-vapid-missing" };

  // 3. Subscribe (or reuse existing subscription)
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(data.publicKey),
    });
  }

  // 4. Persist server-side
  await client.post("/push/subscribe", {
    subscription: sub.toJSON(),
    user_agent: navigator.userAgent,
  });
  return { ok: true };
}

export async function disablePush() {
  if (!pushSupported()) return;
  const sub = await currentSubscription();
  if (!sub) return;
  try { await client.post("/push/unsubscribe", { subscription: sub.toJSON() }); } catch { /* ignore */ }
  try { await sub.unsubscribe(); } catch { /* ignore */ }
}

export async function sendTestPush() {
  return client.post("/push/test");
}
